import { closestPointPolyLine2 } from "geom/closestPoint";
import { DIST_EPSILON_SQUARED } from "geom/core";
import { SNAP_ROUNDING_TOLERANCE } from "geom/polyclip/polyClip";
import {
  polyLine2Copy,
  polyLine2IsClosed,
  polyLine2MergeCoincidentVertices,
  polyLine2VertexCount,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { vec2Dist } from "geom/vec2";

/**
 * Snap `outline` vertices onto nearby room outline geometry within `tolerance`.
 * Room outlines are the source of truth and are not modified.
 *
 * Preference: snap to a room vertex when one is within tolerance; otherwise snap to the
 * closest point on a room edge. Vertices farther than `tolerance` are left unchanged.
 */
export function snapOutlineToRoomOutlines(
  outline: PolyLine2,
  roomOutlines: readonly PolyLine2[],
  tolerance: number = SNAP_ROUNDING_TOLERANCE,
): PolyLine2 {
  if (outline.length < 6 || roomOutlines.length === 0) {
    return polyLine2Copy(outline);
  }

  const result = polyLine2Copy(outline);
  const closed = polyLine2IsClosed(result);
  const vertexCount = polyLine2VertexCount(result);
  const end = closed ? vertexCount - 1 : vertexCount;

  for (let vi = 0; vi < end; vi++) {
    const x = result[vi * 2];
    const y = result[vi * 2 + 1];

    let bestVertexDist = Number.POSITIVE_INFINITY;
    let bestVertexX = x;
    let bestVertexY = y;

    for (const roomOutline of roomOutlines) {
      if (!roomOutline || roomOutline.length < 6) {
        continue;
      }
      const roomClosed = polyLine2IsClosed(roomOutline);
      const roomVertexCount = polyLine2VertexCount(roomOutline);
      const roomEnd = roomClosed ? roomVertexCount - 1 : roomVertexCount;
      for (let rj = 0; rj < roomEnd; rj++) {
        const rx = roomOutline[rj * 2];
        const ry = roomOutline[rj * 2 + 1];
        const d = vec2Dist(x, y, rx, ry);
        if (d < bestVertexDist) {
          bestVertexDist = d;
          bestVertexX = rx;
          bestVertexY = ry;
        }
      }
    }

    if (bestVertexDist <= tolerance) {
      result[vi * 2] = bestVertexX;
      result[vi * 2 + 1] = bestVertexY;
      continue;
    }

    let bestEdgeDist = Number.POSITIVE_INFINITY;
    let bestEdgeX = x;
    let bestEdgeY = y;
    for (const roomOutline of roomOutlines) {
      if (!roomOutline || roomOutline.length < 6) {
        continue;
      }
      const closest = closestPointPolyLine2(roomOutline, x, y);
      if (closest.edgeIndex < 0) {
        continue;
      }
      if (closest.dist < bestEdgeDist) {
        bestEdgeDist = closest.dist;
        bestEdgeX = closest.x;
        bestEdgeY = closest.y;
      }
    }

    if (bestEdgeDist <= tolerance) {
      result[vi * 2] = bestEdgeX;
      result[vi * 2 + 1] = bestEdgeY;
    }
  }

  if (closed) {
    result[result.length - 2] = result[0];
    result[result.length - 1] = result[1];
  }

  // Collapse only true duplicates from closing the polyline / exact snaps — do not
  // merge within `tolerance` or collinear T-vertices on room outlines are removed.
  return polyLine2MergeCoincidentVertices(result, DIST_EPSILON_SQUARED);
}

/**
 * Snap each outline in `outlines` to `roomOutlines` within `tolerance`.
 */
export function snapOutlinesToRoomOutlines(
  outlines: readonly PolyLine2[],
  roomOutlines: readonly PolyLine2[],
  tolerance: number = SNAP_ROUNDING_TOLERANCE,
): PolyLine2[] {
  return outlines.map((outline) => snapOutlineToRoomOutlines(outline, roomOutlines, tolerance));
}
